import React from "react";
import type { Editor as TinyMceEditor } from "tinymce";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import usePostData from "@/hooks/usePostData";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import EditorIntro from "@/pages/parameters/EditorIntro";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FlaskConical, Share2, UserPlus, Users, Wallet } from "lucide-react";

const API_URL = import.meta.env.VITE_API_ENDPOINT;
const EMAIL_TEMPLATES_CACHE_TTL_MS = 1000 * 60 * 60;
const GLOBAL_EMAIL_TEMPLATES_BROWSER_CACHE_KEY = "parameters-global-email-templates";

type StoredTemplate = {
  subject?: string;
  content?: string;
  useVerdeoDefault?: boolean;
};

type ResolvedTemplate = {
  mailType: string;
  source: "verdeo_default" | "carrier_global";
  module: string;
  title: string;
  description: string;
  subject: string;
  content: string | null;
  templatePath: string;
  useVerdeoDefault: boolean;
};

type MailTemplateApiResponse = {
  success: boolean;
  scope: "carrier_global";
  storedTemplates: Record<string, StoredTemplate>;
  resolvedTemplates: Record<string, ResolvedTemplate>;
};

type StarterKey = "native" | "recommended" | "blank";

type StarterTemplate = {
  label: string;
  description: string;
  subject: string;
  content: string;
};

type StarterTemplatesResponse = {
  success: boolean;
  mailType: string;
  templates: Record<StarterKey, StarterTemplate>;
};

type EmailModelItem = {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

type EmailModelGroup = {
  key: string;
  title: string;
  items: EmailModelItem[];
};

type EditorState = {
  mailType: string;
  title: string;
  subject: string;
  content: string;
  useVerdeoDefault: boolean;
  hasStoredTemplate: boolean;
};

type MailVariable = {
  key: string;
  label: string;
  token: string;
  description: string;
  isLink?: boolean;
};

const SUBCONTRACTOR_CREATED_VARIABLES: MailVariable[] = [
  {
    key: "subcontractor_name",
    label: "Nom de l'intervenant",
    token: "{{ subcontractor_name }}",
    description: "Nom de l'intervenant concerné.",
  },
  {
    key: "carrier_name",
    label: "Nom du porteur de projet",
    token: "{{ carrier_name }}",
    description: "Nom du porteur de projet.",
  },
  {
    key: "login_email",
    label: "Email de connexion",
    token: "{{ login_email }}",
    description: "Adresse email utilisée pour se connecter.",
  },
  {
    key: "temporary_password",
    label: "Mot de passe temporaire",
    token: "{{ temporary_password }}",
    description: "Mot de passe communiqué à l'intervenant.",
  },
  {
    key: "login_url",
    label: "Lien de connexion",
    token: "{{ login_url }}",
    description: "Lien vers la page de connexion.",
    isLink: true,
  },
];

const SUBCONTRACTOR_SHARED_LINK_VARIABLES: MailVariable[] = [
  {
    key: "carrier_name",
    label: "Nom du porteur de projet",
    token: "{{ carrier_name }}",
    description: "Nom du porteur de projet.",
  },
  {
    key: "activation_link",
    label: "Lien d'activation",
    token: "{{ activation_link }}",
    description: "Lien transmis à l'intervenant pour finaliser son accès.",
    isLink: true,
  },
];

const COWORKER_CREDENTIAL_VARIABLES: MailVariable[] = [
  {
    key: "coworker_name",
    label: "Nom du collaborateur",
    token: "{{ coworker_name }}",
    description: "Nom du collaborateur concerné.",
  },
  {
    key: "login_email",
    label: "Email de connexion",
    token: "{{ login_email }}",
    description: "Adresse email utilisée pour se connecter.",
  },
  {
    key: "temporary_password",
    label: "Mot de passe temporaire",
    token: "{{ temporary_password }}",
    description: "Mot de passe communiqué au collaborateur.",
  },
  {
    key: "login_url",
    label: "Lien de connexion",
    token: "{{ login_url }}",
    description: "Lien vers la page de connexion adaptée.",
    isLink: true,
  },
];

const COWORKER_SHARED_LINK_VARIABLES: MailVariable[] = [
  {
    key: "carrier_name",
    label: "Nom du porteur de projet",
    token: "{{ carrier_name }}",
    description: "Nom du porteur de projet.",
  },
  {
    key: "activation_link",
    label: "Lien d'activation",
    token: "{{ activation_link }}",
    description: "Lien transmis au collaborateur pour activer son accès.",
    isLink: true,
  },
];

const FUNDER_ACCESS_VARIABLES: MailVariable[] = [
  {
    key: "funder_name",
    label: "Nom du financeur",
    token: "{{ funder_name }}",
    description: "Nom du financeur concerné.",
  },
  {
    key: "funder_email",
    label: "Email du financeur",
    token: "{{ funder_email }}",
    description: "Adresse email utilisée par le financeur.",
  },
  {
    key: "temporary_password",
    label: "Mot de passe temporaire",
    token: "{{ temporary_password }}",
    description: "Mot de passe temporaire communiqué au financeur.",
  },
  {
    key: "login_url",
    label: "Lien de connexion",
    token: "{{ login_url }}",
    description: "Lien vers la plateforme financeur.",
    isLink: true,
  },
];

const GLOBAL_EMAIL_GROUPS: EmailModelGroup[] = [
  {
    key: "subcontractor",
    title: "Intervenant",
    items: [
      {
        key: "subcontractor_created",
        title: "Création de compte intervenant",
        description: "Email envoyé à l'intervenant lors de la création de son compte.",
        icon: UserPlus,
      },
      {
        key: "subcontractor_shared_link",
        title: "Partage de lien intervenant",
        description: "Email envoyé lorsqu'un intervenant reçoit un lien partagé.",
        icon: Share2,
      },
    ],
  },
  {
    key: "coworker",
    title: "Collaborateur",
    items: [
      {
        key: "coworker_created",
        title: "Création de compte collaborateur",
        description: "Email envoyé lorsqu'un collaborateur est créé par le Porteur de Projet.",
        icon: Users,
      },
      {
        key: "coworker_registration_success",
        title: "Inscription collaborateur confirmée",
        description: "Email de confirmation après validation de l'inscription d'un collaborateur.",
        icon: CheckCircle2,
      },
      {
        key: "coworker_shared_link",
        title: "Partage de lien collaborateur",
        description: "Email envoyé lorsqu'un collaborateur reçoit un lien partagé.",
        icon: Share2,
      },
    ],
  },
  {
    key: "funder",
    title: "Financeur",
    items: [
      {
        key: "funder_access_created",
        title: "Création d'accès financeur",
        description: "Email envoyé au financeur lors de la création de son accès.",
        icon: Wallet,
      },
      {
        key: "funder_access_updated",
        title: "Modification d'accès financeur",
        description: "Email envoyé au financeur lorsque ses accès sont modifiés.",
        icon: Wallet,
      },
    ],
  },
  {
    key: "notification",
    title: "Suivi / notifications",
    items: [],
  },
].filter((group) => group.items.length > 0);

const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-3xl border border-[#dfe9d8] bg-white p-5 shadow-[0_8px_30px_rgba(29,82,20,0.05)] md:p-6">
    <div className="mb-5 flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-[#18351b]">{title}</h2>
      <p className="max-w-3xl text-sm text-[#5f6d5c]">{description}</p>
    </div>
    {children}
  </section>
);

const EmailCards = ({
  items,
  onConfigure,
  sourceByKey,
}: {
  items: EmailModelItem[];
  onConfigure?: (item: EmailModelItem) => void;
  sourceByKey?: Record<string, "verdeo_default" | "carrier_global">;
}) => (
  <div className="max-h-[340px] overflow-y-auto pr-1">
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon;
        const source = sourceByKey?.[item.key];
        const isCustomized = source === "carrier_global";

        return (
          <div
            key={item.key}
            className="flex h-full flex-col rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] p-4 transition-colors hover:border-[#c7d8bb] hover:bg-white"
          >
            <div className="mb-3 flex items-start gap-3">
              <div className="rounded-2xl bg-[#edf6e7] p-2.5 text-[#486a2f]">
                <Icon className="size-4.5" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#18351b]">{item.title}</h4>
                {source && (
                  <span
                    className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      isCustomized
                        ? "bg-[#edf6e7] text-[#486a2f]"
                        : "bg-[#eef1f4] text-[#64707d]"
                    }`}
                  >
                    {isCustomized ? "Personnalisé" : "Verdeo"}
                  </span>
                )}
                <p className="mt-1 min-h-[60px] text-xs leading-5 text-[#6c796a]">
                  {item.description}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-auto w-full border-[#d6e4cb] text-[#2f4b2d] hover:border-[#486a2f] hover:bg-[#edf6e7] hover:text-[#2d5a23]"
              onClick={() => onConfigure?.(item)}
            >
              Configurer
            </Button>
          </div>
        );
      })}
    </div>
  </div>
);

const EmailGroupAccordion = ({
  groups,
  valuePrefix,
  onConfigure,
  sourceByKey,
}: {
  groups: EmailModelGroup[];
  valuePrefix: string;
  onConfigure?: (item: EmailModelItem) => void;
  sourceByKey?: Record<string, "verdeo_default" | "carrier_global">;
}) => (
  <Accordion type="multiple" className="space-y-3">
    {groups.map((group) => (
      <AccordionItem
        key={`${valuePrefix}-${group.key}`}
        value={`${valuePrefix}-${group.key}`}
        className="overflow-hidden rounded-2xl border border-[#e1eadb] bg-[#fbfdf9] px-4"
      >
        <AccordionTrigger className="py-4 text-left hover:no-underline">
          <div className="flex min-w-0 items-center gap-3">
            <span className="text-sm font-semibold text-[#18351b]">{group.title}</span>
            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-[#6c796a]">
              {group.items.length} modèle{group.items.length > 1 ? "s" : ""}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-4">
          <EmailCards items={group.items} onConfigure={onConfigure} sourceByKey={sourceByKey} />
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

function ParametersGlobalEmailTemplates() {
  const editorInstanceRef = React.useRef<TinyMceEditor | null>(null);
  const queryClient = useQueryClient();
  const templatesEndpoint = `${API_URL}/mail-templates/global`;
  const templatesQuery = useFetchDataV2<MailTemplateApiResponse | null>(templatesEndpoint, {
    staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
    gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: GLOBAL_EMAIL_TEMPLATES_BROWSER_CACHE_KEY,
    browserCacheTtlMs: EMAIL_TEMPLATES_CACHE_TTL_MS,
  });
  const { postData, loading: isSaving } = usePostData<any>();
  const { postData: postTestData, loading: isSendingTest } = usePostData<any>();
  const [editorState, setEditorState] = React.useState<EditorState | null>(null);
  const [testDialogOpen, setTestDialogOpen] = React.useState(false);
  const [testEmail, setTestEmail] = React.useState("");
  const [starterCache, setStarterCache] = React.useState<Record<string, StarterTemplatesResponse>>({});
  const [loadingStarterKey, setLoadingStarterKey] = React.useState<StarterKey | null>(null);
  const activeVariables = React.useMemo<MailVariable[]>(
    () => {
      if (!editorState) return [];
      if ("subcontractor_created" === editorState.mailType) {
        return SUBCONTRACTOR_CREATED_VARIABLES;
      }
      if ("subcontractor_shared_link" === editorState.mailType) {
        return SUBCONTRACTOR_SHARED_LINK_VARIABLES;
      }
      if (
        "coworker_created" === editorState.mailType ||
        "coworker_registration_success" === editorState.mailType
      ) {
        return COWORKER_CREDENTIAL_VARIABLES;
      }
      if ("coworker_shared_link" === editorState.mailType) {
        return COWORKER_SHARED_LINK_VARIABLES;
      }
      if (
        "funder_access_created" === editorState.mailType ||
        "funder_access_updated" === editorState.mailType
      ) {
        return FUNDER_ACCESS_VARIABLES;
      }
      return [];
    },
    [editorState],
  );

  const sourceByKey = React.useMemo<Record<string, "verdeo_default" | "carrier_global">>(
    () =>
      Object.fromEntries(
        Object.entries(templatesQuery.data?.resolvedTemplates ?? {}).map(([key, template]) => [
          key,
          template.source,
        ]),
      ),
    [templatesQuery.data?.resolvedTemplates],
  );

  React.useEffect(() => {
    const body = document.body;
    if (!body) return;

    if (editorState) {
      body.classList.add("pointer-events-initial");
    } else {
      body.classList.remove("pointer-events-initial");
    }

    return () => {
      body.classList.remove("pointer-events-initial");
    };
  }, [editorState]);

  const handleOpenEditor = React.useCallback(
    (item: EmailModelItem) => {
      const data = templatesQuery.data;
      if (!data) {
        toast({ title: "Chargement des modèles en cours." });
        return;
      }

      const stored = data.storedTemplates[item.key];
      const resolved = data.resolvedTemplates[item.key];

      setEditorState({
        mailType: item.key,
        title: item.title,
        subject: stored?.subject ?? resolved?.subject ?? "",
        content: stored?.content ?? resolved?.content ?? "",
        useVerdeoDefault: !stored,
        hasStoredTemplate: !!stored,
      });
    },
    [templatesQuery.data],
  );

  const handleSaveEditor = React.useCallback(async () => {
    if (!editorState) return;

    const previousTemplates = templatesQuery.data ?? null;
    const editorSnapshot = editorState;
    const applyTemplateUpdate = (
      current: MailTemplateApiResponse | null | undefined,
      serverResult?: any,
    ) => {
      if (!current) return current;

      const storedTemplates = {
        ...current.storedTemplates,
        ...(serverResult?.storedTemplates ?? {}),
      };

      if (editorSnapshot.useVerdeoDefault) {
        delete storedTemplates[editorSnapshot.mailType];
      } else {
        storedTemplates[editorSnapshot.mailType] = {
          subject: editorSnapshot.subject,
          content: editorSnapshot.content,
          useVerdeoDefault: false,
        };
      }

      const currentResolved = current.resolvedTemplates[editorSnapshot.mailType];
      const nextResolved =
        serverResult?.resolvedTemplate ??
        (currentResolved
          ? {
              ...currentResolved,
              subject: editorSnapshot.subject,
              content: editorSnapshot.content,
              useVerdeoDefault: editorSnapshot.useVerdeoDefault,
              source: editorSnapshot.useVerdeoDefault
                ? "verdeo_default"
                : "carrier_global",
            }
          : currentResolved);

      return {
        ...current,
        storedTemplates,
        resolvedTemplates: nextResolved
          ? {
              ...current.resolvedTemplates,
              [editorSnapshot.mailType]: nextResolved,
            }
          : current.resolvedTemplates,
      };
    };

    queryClient.setQueryData(
      ["fetchDataV2", templatesEndpoint],
      (current: MailTemplateApiResponse | null | undefined) =>
        applyTemplateUpdate(current ?? previousTemplates),
    );
    setEditorState(null);

    const result = await postData({
      url: templatesEndpoint,
      data: {
        mailType: editorSnapshot.mailType,
        subject: editorSnapshot.subject,
        content: editorSnapshot.content,
        useVerdeoDefault: editorSnapshot.useVerdeoDefault,
      },
    });

    if (!result?.success) {
      queryClient.setQueryData(["fetchDataV2", templatesEndpoint], previousTemplates);
      setEditorState(editorSnapshot);
      toast({ title: "La sauvegarde du modèle a échoué." });
      return;
    }

    queryClient.setQueryData(
      ["fetchDataV2", templatesEndpoint],
      (current: MailTemplateApiResponse | null | undefined) =>
        applyTemplateUpdate(current ?? previousTemplates, result),
    );
    toast({ title: "Modèle enregistré." });
  }, [editorState, postData, queryClient, templatesEndpoint, templatesQuery.data]);

  const handleOpenTestDialog = React.useCallback(() => {
    const editor = editorInstanceRef.current;
    if (editor) {
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
    }
    setTestDialogOpen(true);
  }, []);

  const loadStarterTemplates = React.useCallback(
    async (mailType: string) => {
      if (starterCache[mailType]) {
        return starterCache[mailType];
      }

      const response = await templatesQuery.fetchData(
        `${API_URL}/mail-templates/global/${mailType}/starters`,
        {
          queryKey: ["parameters-global-email-template-starters", mailType],
          staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
          gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
        },
      );

      if (!response?.success) {
        toast({ title: "Impossible de charger les modèles de départ." });
        return null;
      }

      const data = response as StarterTemplatesResponse;
      setStarterCache((prev) => ({ ...prev, [mailType]: data }));
      return data;
    },
    [API_URL, starterCache, templatesQuery],
  );

  const handleSendTestEmail = React.useCallback(async () => {
    if (!editorState) return;

    const editor = editorInstanceRef.current;
    let subject = editorState.subject;
    let content = editor && !editorState.useVerdeoDefault ? editor.getContent() : editorState.content;
    const testEmailSnapshot = testEmail;

    if (editorState.useVerdeoDefault) {
      setTestDialogOpen(false);
      setTestEmail("");
      toast({ title: "Mail natif de test envoyé." });

      const result = await postTestData({
        url: `${API_URL}/mail-templates/test-send`,
        data: {
          email: testEmailSnapshot,
          mailType: editorState.mailType,
          scope: "global",
          native: true,
        },
      });

      if (!result?.success) {
        setTestEmail(testEmailSnapshot);
        setTestDialogOpen(true);
        toast({ title: result?.message || "L'envoi du mail natif de test a échoué." });
        return;
      }

      return;
    }

    if (!content.trim()) {
      setLoadingStarterKey("native");
      const data = await loadStarterTemplates(editorState.mailType);
      setLoadingStarterKey(null);
      const nativeTemplate = data?.templates?.native;

      if (nativeTemplate) {
        subject = nativeTemplate.subject || subject;
        content = nativeTemplate.content || content;
      }
    }

    if (!content.trim()) {
      toast({ title: "Impossible de charger le contenu natif du mail." });
      return;
    }

    setTestDialogOpen(false);
    setTestEmail("");
    toast({ title: "Mail de test envoyé." });

    const result = await postTestData({
      url: `${API_URL}/mail-templates/test-send`,
      data: {
        email: testEmailSnapshot,
        mailType: editorState.mailType,
        subject,
        content,
      },
    });

    if (!result?.success) {
      setTestEmail(testEmailSnapshot);
      setTestDialogOpen(true);
      toast({ title: result?.message || "L'envoi du mail de test a échoué." });
      return;
    }
  }, [API_URL, editorState, loadStarterTemplates, postTestData, testEmail]);

  const handleToggleUseDefault = React.useCallback(
    async (checked: boolean) => {
      const current = editorState;
      if (!current) return;

      if (checked) {
        setEditorState((prev) => (prev ? { ...prev, useVerdeoDefault: true } : prev));
        return;
      }

      setEditorState((prev) =>
        prev
          ? {
              ...prev,
              useVerdeoDefault: false,
            }
          : prev,
      );

      if (!current.hasStoredTemplate) {
        setLoadingStarterKey("native");
        const data = await loadStarterTemplates(current.mailType);
        setLoadingStarterKey(null);
        const template = data?.templates?.native;
        if (!template) {
          toast({ title: "Modèle natif indisponible." });
          return;
        }

        setEditorState((prev) =>
          prev
            ? {
                ...prev,
                subject: template.subject,
                content: template.content,
                useVerdeoDefault: false,
              }
            : prev,
        );
      }
    },
    [editorState, loadStarterTemplates],
  );

  const handleTinyMceOutsideInteraction = React.useCallback((event: Event) => {
    const target = event.target as HTMLElement | null;
    const tinyPopupOpen = !!document.querySelector(
      ".tox-dialog, .tox-dialog-wrap, .tox-tinymce-aux, .tox-silver-sink, .tox-menu, .tox-collection, .tox-pop",
    );

    if (tinyPopupOpen) {
      event.preventDefault();
      return;
    }

    if (
      target?.closest(
        ".tox-tinymce-aux, .tox-silver-sink, .tox-dialog, .tox-dialog-wrap, .tox-menu, .tox-collection, .tox-pop",
      )
    ) {
      event.preventDefault();
    }

    if (target?.closest("[data-email-test-dialog]")) {
      event.preventDefault();
    }
  }, []);

  const handleInsertVariable = React.useCallback((token: string) => {
    if (!editorState || editorState.useVerdeoDefault) return;

    const editor = editorInstanceRef.current;
    if (editor) {
      editor.insertContent(token);
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
      return;
    }

    setEditorState((prev) =>
      prev ? { ...prev, content: prev.content ? `${prev.content} ${token}` : token } : prev,
    );
  }, [editorState]);

  const handleInsertLinkVariable = React.useCallback((token: string) => {
    if (!editorState || editorState.useVerdeoDefault) return;

    const html = `<a href="${token}">${token}</a>`;
    const editor = editorInstanceRef.current;
    if (editor) {
      editor.insertContent(html);
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
      return;
    }

    setEditorState((prev) =>
      prev ? { ...prev, content: prev.content ? `${prev.content} ${html}` : html } : prev,
    );
  }, [editorState]);

  return (
    <SectionCard
      title="Emails globaux"
      description="Ces modèles couvrent les emails généraux de votre espace. Par défaut, ils reprennent les modèles Verdeo jusqu’à ce qu’une personnalisation soit définie."
    >
      {templatesQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="rounded-2xl border border-[#e6eee0] bg-[#fbfdf9] p-4">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-3 h-4 w-72" />
            </div>
          ))}
        </div>
      ) : (
        <EmailGroupAccordion
          groups={GLOBAL_EMAIL_GROUPS}
          valuePrefix="global-mail"
          onConfigure={handleOpenEditor}
          sourceByKey={sourceByKey}
        />
      )}

      <Dialog modal={false} open={!!editorState} onOpenChange={(open) => !open && setEditorState(null)}>
        <DialogContent
          id="global-email-template-dialog-content"
          className="max-w-4xl"
          staticOverlay
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={handleTinyMceOutsideInteraction}
          onPointerDownOutside={handleTinyMceOutsideInteraction}
          onFocusOutside={handleTinyMceOutsideInteraction}
        >
          <DialogHeader>
            <DialogTitle>{editorState?.title}</DialogTitle>
            <DialogDescription>
              Ce modèle s'applique à l'ensemble de votre espace, hors appels à projets.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-2xl border border-[#dfe9d8] bg-[#f8fbf5] px-4 py-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#18351b]">Utiliser les emails de Verdeo</p>
                <p className="text-xs text-[#6c796a]">
                  Si activé, ce modèle hérite du mail par défaut déjà en place.
                </p>
              </div>
              <Switch
                checked={editorState?.useVerdeoDefault ?? true}
                onCheckedChange={handleToggleUseDefault}
              />
            </div>

            {editorState?.useVerdeoDefault ? (
              <div className="rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] px-4 py-5 text-sm text-[#5f6d5c]">
                <p>Les contenus par défaut de Verdeo seront utilisés pour cet email.</p>
                <Button
                  type="button"
                  className="mt-4 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={!editorState || isSendingTest}
                  onClick={handleOpenTestDialog}
                >
                  <FlaskConical className="size-4" />
                  Tester le modèle
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div
                  className={
                    activeVariables.length > 0
                      ? "grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]"
                      : "space-y-5"
                  }
                >
                  <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="global-email-template-subject">Objet</Label>
                    <Input
                      id="global-email-template-subject"
                      value={editorState?.subject ?? ""}
                      onChange={(event) =>
                        setEditorState((prev) =>
                          prev ? { ...prev, subject: event.target.value } : prev,
                        )
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="global-email-template-content">Contenu</Label>
                    <EditorIntro
                      value={editorState?.content ?? ""}
                      height={360}
                      uiContainerSelector="#global-email-template-dialog-content"
                      onReady={(editor) => {
                        editorInstanceRef.current = editor;
                      }}
                      onChange={(value) =>
                        setEditorState((prev) => (prev ? { ...prev, content: value } : prev))
                      }
                    />
                  </div>
                  </div>

                  {activeVariables.length > 0 && (
                    <aside className="rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] p-4">
                    <Button
                      type="button"
                      className="mb-4 w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                      disabled={!editorState || editorState.useVerdeoDefault || !!loadingStarterKey}
                      onClick={handleOpenTestDialog}
                    >
                      <FlaskConical className="size-4" />
                      Tester le modèle
                    </Button>
                    <div className="space-y-1">
                      <h3 className="text-sm font-semibold text-[#18351b]">Variables disponibles</h3>
                      <p className="text-xs leading-5 text-[#6c796a]">
                        Clique sur une variable pour l’insérer dans le contenu du mail.
                      </p>
                    </div>

                    <div className="mt-4 space-y-2 pr-1">
                      {activeVariables.map((variable) => (
                        <div
                          key={variable.key}
                          className="rounded-xl border border-[#dfe9d8] bg-white px-3 py-2"
                        >
                          <p className="text-sm font-medium text-[#18351b]">{variable.label}</p>
                          <p className="mt-1 text-xs leading-5 text-[#6c796a]">{variable.description}</p>
                          <code className="mt-2 block rounded-md bg-[#eef4e8] px-2 py-1 text-[11px] text-[#36552a]">
                            {variable.token}
                          </code>
                          <div className="mt-3 flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 border-[#dfe9d8] text-[#2f4b2d] hover:border-[#bfd2b2] hover:bg-[#f4f8ef]"
                              onClick={() => handleInsertVariable(variable.token)}
                            >
                              Insérer
                            </Button>
                            {variable.isLink && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 border-[#dfe9d8] text-[#2f4b2d] hover:border-[#bfd2b2] hover:bg-[#f4f8ef]"
                                onClick={() => handleInsertLinkVariable(variable.token)}
                              >
                                Insérer en lien
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    </aside>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditorState(null)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#486a2f] text-white hover:bg-[#3d5a27]"
              disabled={isSaving || !!loadingStarterKey}
              onClick={handleSaveEditor}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen} modal={false}>
        <DialogContent
          className="z-[10001] max-w-md"
          staticOverlay
          overlayClassName="z-[10000]"
          data-email-test-dialog
        >
          <DialogHeader>
            <DialogTitle>Tester le modèle</DialogTitle>
            <DialogDescription>
              Indiquez l’adresse email qui recevra ce modèle avec des données fictives.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="global-email-template-test-recipient">Email destinataire</Label>
            <Input
              id="global-email-template-test-recipient"
              type="email"
              value={testEmail}
              placeholder="exemple@domaine.fr"
              onChange={(event) => setTestEmail(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTestDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#486a2f] text-white hover:bg-[#3d5a27]"
              disabled={isSendingTest || !testEmail.trim()}
              onClick={handleSendTestEmail}
            >
              {isSendingTest ? "Envoi..." : "Envoyer le test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

export default ParametersGlobalEmailTemplates;
